import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@freelancer-os/ui/src/components/ui/card';
import { cn } from '@freelancer-os/ui/src/lib/utils';
import {
  Calculator,
  FileText,
  Paperclip,
  Receipt,
  Users,
  Zap,
} from 'lucide-react';

interface Example {
  text: string;
  /** When set, a "📎 <label>" hint fades in below the prompt at the pause phase. */
  attachFile?: string;
}

interface Category {
  key: 'invoice' | 'expense' | 'client' | 'tax';
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  examples: Example[];
}

// Vietnamese-only on purpose — prompts demonstrate VN-specific phrasing
// ("anh Tùng", "mộc 1 tỷ", "tao đóng thuế"...) and translating loses the voice.
// Only the structural strings (greeting + card titles) go through i18n.
const CATEGORIES: Category[] = [
  {
    key: 'invoice',
    icon: FileText,
    examples: [
      { text: 'Tạo hoá đơn 5tr cho anh Tùng làm web' },
      { text: 'Soạn tin nhắc khách INV-2026-XYZ' },
      { text: 'Gửi hoá đơn này qua email' },
    ],
  },
  {
    key: 'expense',
    icon: Receipt,
    examples: [
      { text: 'Ghi chi phí từ hoá đơn này', attachFile: 'kéo thả file' },
      { text: 'Thu chi tháng này?' },
      { text: 'Ghi 5tr tiền mặt khách Hùng vừa trả' },
    ],
  },
  {
    key: 'client',
    icon: Users,
    examples: [
      { text: 'Anh Tùng nợ bao nhiêu?' },
      { text: 'Lịch sử giao dịch Studio ABC' },
      { text: 'Import danh sách khách', attachFile: 'kéo thả CSV' },
    ],
  },
  {
    key: 'tax',
    icon: Calculator,
    examples: [
      { text: 'Tao đóng thuế tháng này bao nhiêu?' },
      { text: 'Còn cách mốc 1 tỷ bao xa?' },
      { text: 'Xuất sổ chi tiết Q2 cho kế toán' },
    ],
  },
];

type Phase = 'wait' | 'typing' | 'pause' | 'erasing';

interface TypewriterState {
  text: string;
  showCursor: boolean;
  /** Visible chip on the card — only set during the pause phase so it fades with the prompt. */
  attachFile: string | null;
  /** Tells the click handler what to fill — the full current example, not the partial typed text. */
  fullText: string;
  /** Current example's attachFile label regardless of phase — used by the click handler to seed the chatbox hint. */
  currentAttachFile: string | null;
}

function useTypewriter(examples: Example[], initialDelayMs: number): TypewriterState {
  const [exampleIdx, setExampleIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>('wait');
  const [charIdx, setCharIdx] = useState(0);

  useEffect(() => {
    if (phase !== 'wait') return;
    const t = window.setTimeout(() => setPhase('typing'), initialDelayMs);
    return () => clearTimeout(t);
  }, [phase, initialDelayMs]);

  useEffect(() => {
    const cur = examples[exampleIdx];
    if (!cur || phase === 'wait') return;
    let timer: number | undefined;
    if (phase === 'typing') {
      if (charIdx < cur.text.length) {
        timer = window.setTimeout(() => setCharIdx((i) => i + 1), 30);
      } else {
        // Defer the phase transition so the effect doesn't setState synchronously.
        timer = window.setTimeout(() => setPhase('pause'), 0);
      }
    } else if (phase === 'pause') {
      timer = window.setTimeout(() => setPhase('erasing'), 3000);
    } else if (phase === 'erasing') {
      if (charIdx > 0) {
        timer = window.setTimeout(() => setCharIdx((i) => i - 1), 15);
      } else {
        timer = window.setTimeout(() => {
          setExampleIdx((i) => (i + 1) % examples.length);
          setPhase('typing');
        }, 0);
      }
    }
    return () => clearTimeout(timer);
  }, [phase, charIdx, exampleIdx, examples]);

  const cur = examples[exampleIdx];
  return {
    text: cur ? cur.text.slice(0, charIdx) : '',
    showCursor: phase === 'typing' || phase === 'pause',
    attachFile: phase === 'pause' && cur?.attachFile ? cur.attachFile : null,
    fullText: cur?.text ?? '',
    currentAttachFile: cur?.attachFile ?? null,
  };
}

interface SuggestionCardProps {
  category: Category;
  delayMs: number;
  onPick: (text: string, attachLabel?: string) => void;
}

function SuggestionCard({ category, delayMs, onPick }: SuggestionCardProps) {
  const { t } = useTranslation('chat');
  const tw = useTypewriter(category.examples, delayMs);
  const Icon = category.icon;

  return (
    <Card
      onClick={() => tw.fullText && onPick(tw.fullText, tw.currentAttachFile ?? undefined)}
      className="cursor-pointer border-surface-200 transition-all hover:border-brand-400 hover:shadow-[0_0_0_3px_rgba(22,163,74,0.08)] group"
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-md bg-brand-50 text-brand-600 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
            <Icon className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="text-[13px] font-semibold text-surface-800">
            {t(`suggestions.categories.${category.key}`)}
          </span>
        </div>
        <p className="text-[13px] text-surface-500 italic leading-relaxed min-h-[42px]">
          <span>{tw.text}</span>
          <span
            className={cn(
              'inline-block w-[2px] h-[0.9em] align-text-bottom ml-px bg-brand-500',
              tw.showCursor ? 'animate-[blink-cursor_0.8s_step-end_infinite]' : 'opacity-0',
            )}
            aria-hidden="true"
          />
        </p>
        {tw.attachFile && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-[11px] font-medium text-amber-700 animate-[fade-up_0.25s_ease-out]">
            <Paperclip className="w-3 h-3" strokeWidth={2} />
            {tw.attachFile}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function SuggestionGrid({
  onPick,
}: {
  onPick: (text: string, attachLabel?: string) => void;
}) {
  const { t } = useTranslation('chat');
  return (
    <div className="max-w-[720px] mx-auto py-8 px-2">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-brand-400 to-emerald-500 mb-3">
          <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <h2 className="text-lg font-semibold text-surface-800">
          {t('suggestions.greeting')}
        </h2>
        <p className="text-[13px] text-surface-500 mt-1">{t('suggestions.subtitle')}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {CATEGORIES.map((cat, i) => (
          <SuggestionCard
            key={cat.key}
            category={cat}
            delayMs={i * 400}
            onPick={onPick}
          />
        ))}
      </div>
    </div>
  );
}
